import { useEffect, useMemo, useState } from 'react';
import { openUrlInNewTab } from '../utils/openDocument';
import { Link, useSearchParams } from 'react-router-dom';
import { useCrm } from '../store';
import { Card, PageTitle } from '../ui';
import { formatDateFR, formatEUR } from '../utils';
import { Calendar, FileSignature, Download, Send, Loader2, FileText, Plus, Edit3, RefreshCcw, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useStructureFilter, STRUCTURE_LABELS } from '../structureContext';
import { StructureBadge } from '../components/StructureSelector';
import { supabase } from '@/integrations/supabase/client';
import { resetRelanceNotificationForPrestation } from '../utils/relanceNotif';
import {
  CONTRAT_STATUTS,
  STATUTS_TRANSMIS_GROUP,
  STATUTS_SIGNES_GROUP,
  STATUTS_PERDUS_GROUP,
  normalizeContratStatut,
  contratStatutClass,
} from '../constants/contratStatuts';

interface ContratRow {
  id: string;
  numero: string;
  structure: 'cygnature' | 'suivez_vos_reves';
  prestation_id: string | null;
  proposition_id: string | null;
  client_id: string | null;
  client_nom: string;
  client_email: string | null;
  date_event: string | null;
  objet: string | null;
  montant_ttc: number | null;
  montant_ht: number | null;
  statut: string;
  pdf_url: string | null;
  contenu: string | null;

  date_emission: string;
  created_at: string;
}

interface PropVersion {
  id: string;
  numero: string | null;
  version: number;
  statut: string;
  date_emission: string;
  prestation_id: string | null;
}

interface PrestationRef {
  id: string;
  numero: string | null;
}

export default function Contrats() {
  const data = useCrm();
  const { matches: matchesStructure, filter } = useStructureFilter();
  const [searchParams] = useSearchParams();
  const initialSubtab = searchParams.get('subtab');
  const validSubtab = (['a-generer', 'transmis', 'signes', 'perdus'] as const).includes(initialSubtab as any)
    ? (initialSubtab as 'a-generer' | 'transmis' | 'signes' | 'perdus')
    : 'a-generer';
  const [contrats, setContrats] = useState<ContratRow[]>([]);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [tab, setTab] = useState<'a-generer' | 'transmis' | 'signes' | 'perdus'>(validSubtab);
  const [propsByPrestation, setPropsByPrestation] = useState<Map<string, PropVersion[]>>(new Map());
  const [prestationRefs, setPrestationRefs] = useState<PrestationRef[]>([]);
  const [loading, setLoading] = useState(true);

  const loadContrats = async () => {
    setLoading(true);
    const { data: rows } = await supabase
      .from('contrats').select('*').is('deleted_at', null)
      .order('date_emission', { ascending: false })
      .order('created_at', { ascending: false });
    setContrats((rows || []) as ContratRow[]);
    setLoading(false);
  };
  const loadPropositions = async () => {
    const { data: rows } = await supabase
      .from('propositions')
      .select('id, numero, version, statut, date_emission, prestation_id')
      .not('prestation_id', 'is', null)
      .order('version', { ascending: true });
    const map = new Map<string, PropVersion[]>();
    (rows || []).forEach((r: any) => {
      const arr = map.get(r.prestation_id) || [];
      arr.push(r as PropVersion);
      map.set(r.prestation_id, arr);
    });
    setPropsByPrestation(map);
  };
  const loadPrestationRefs = async () => {
    const { data: rows } = await supabase
      .from('prestations')
      .select('id, numero')
      .is('deleted_at', null);
    setPrestationRefs((rows || []) as PrestationRef[]);
  };
  useEffect(() => { loadContrats(); loadPropositions(); loadPrestationRefs(); }, []);

  const prestationDbIdByNumero = useMemo(() => {
    const map = new Map<string, string>();
    prestationRefs.forEach((p) => { if (p.numero) map.set(p.numero, p.id); });
    return map;
  }, [prestationRefs]);

  const prestationNumeroByDbId = useMemo(() => {
    const map = new Map<string, string>();
    prestationRefs.forEach((p) => { if (p.numero) map.set(p.id, p.numero); });
    return map;
  }, [prestationRefs]);

  const getPrestationDbId = (prestationNumero: string) => prestationDbIdByNumero.get(prestationNumero) || prestationNumero;
  const getPrestationParam = (prestationId: string | null) => prestationId ? (prestationNumeroByDbId.get(prestationId) || prestationId) : '';
  const getVersionsForPrestation = (prestationNumero: string) => (
    propsByPrestation.get(getPrestationDbId(prestationNumero)) || propsByPrestation.get(prestationNumero) || []
  );

  const candidatesAGenerer = useMemo(
    () => data.prest
      .filter((p) => matchesStructure(p.structure))
      .filter((p) => !p.archivedAt)
      .filter((p) => !/Refus|Annul|Réalis|Payé\s*&&\s*Livré/i.test(p.statut))
      .filter((p) => getVersionsForPrestation(p.id).length > 0 || /Propal|Relance|Contrat|Sign/i.test(p.statut))
      .filter((p) => !contrats.some((c) => c.prestation_id === getPrestationDbId(p.id) || c.prestation_id === p.id))
      .sort((a, b) => {
        const latestA = getVersionsForPrestation(a.id).reduce((max, v) =>
          v.date_emission && v.date_emission > max ? v.date_emission : max, a.updatedAt || '');
        const latestB = getVersionsForPrestation(b.id).reduce((max, v) =>
          v.date_emission && v.date_emission > max ? v.date_emission : max, b.updatedAt || '');
        return latestB.localeCompare(latestA);
      }),
    [data.prest, matchesStructure, contrats, propsByPrestation, prestationDbIdByNumero]
  );
  const contratsFiltres = contrats.filter((c) => filter === 'all' || c.structure === filter);
  const inGroup = (c: ContratRow, group: readonly string[]) =>
    group.includes(normalizeContratStatut(c.statut));
  const transmis = contratsFiltres.filter((c) => inGroup(c, STATUTS_TRANSMIS_GROUP));
  const signes = contratsFiltres.filter((c) => inGroup(c, STATUTS_SIGNES_GROUP));
  const perdus = contratsFiltres.filter((c) => inGroup(c, STATUTS_PERDUS_GROUP));

  const changerStatut = async (c: ContratRow, statut: string) => {
    const { error } = await supabase.from('contrats').update({ statut }).eq('id', c.id);
    if (error) { toast.error(`Échec : ${error.message}`); return; }
    toast.success(`Statut → ${statut}`);
    await loadContrats();
  };

  const totalHT = (c: ContratRow) => {
    if (c.montant_ht != null && c.montant_ht > 0) return c.montant_ht;
    if (c.montant_ttc != null && c.montant_ttc > 0) return c.montant_ttc / 1.2;
    return 0;
  };

  const getPrestationForContrat = (c: ContratRow) => {
    if (!c.prestation_id) return null;
    const numero = prestationNumeroByDbId.get(c.prestation_id);
    return data.prest.find((p) => p.id === numero) || null;
  };

  const eventLabel = (c: ContratRow) => {
    const prest = getPrestationForContrat(c);
    const type = (prest?.themes?.filter(Boolean).join(' · ') || '') || prest?.libelle || c.objet || '—';
    const lieu = prest?.lieu || '—';
    return { type, lieu };
  };

  const contratData = (c: ContratRow): any | null => {
    if (!c.contenu) return null;
    if (typeof c.contenu !== 'string') return c.contenu;
    try {
      return JSON.parse(c.contenu);
    } catch {
      // Contenu historique éventuellement composé de plusieurs objets JSON
      // concaténés (`}{`) : on les fusionne dans l'ordre.
      try {
        const parts = c.contenu.replace(/\}\s*\{/g, '}\u0000{').split('\u0000');
        const merged = parts.reduce<any>((acc, part) => {
          try { return { ...acc, ...JSON.parse(part) }; } catch { return acc; }
        }, {});
        return Object.keys(merged).length ? merged : null;
      } catch {
        return null;
      }
    }
  };


  const fileNameFor = (c: ContratRow) =>
    `${c.numero || 'contrat'}-${(c.client_nom || 'client').replace(/[^a-z0-9]+/gi, '-')}.pdf`;

  /** Récupère (ou génère puis stocke) le PDF du contrat. Renvoie une URL signée. */
  const ensurePdfUrl = async (c: ContratRow, expiresIn = 3600): Promise<string | null> => {
    if (!c.pdf_url) {
      const form = contratData(c);
      if (!form) throw new Error("Contenu du contrat introuvable — rouvrez le contrat puis ré-enregistrez-le.");
      const { genererContratPdfBlob } = await import('../contract/generatePdf');
      const blob = await genererContratPdfBlob(c.structure, form, fileNameFor(c));
      const path = `clients/${c.id}/${Date.now()}-${fileNameFor(c)}`;
      const { error: upErr } = await supabase.storage
        .from('contrats-pdf')
        .upload(path, blob, { upsert: true, contentType: 'application/pdf' });
      if (upErr) throw upErr;
      await supabase.from('contrats').update({ pdf_url: path }).eq('id', c.id);
      c.pdf_url = path;
    }
    const { data: signed } = await supabase.storage.from('contrats-pdf').createSignedUrl(c.pdf_url, expiresIn);
    return signed?.signedUrl || null;
  };

  const telecharger = async (c: ContratRow) => {
    setLoadingId(c.id);
    try {
      if (c.pdf_url) {
        const url = await ensurePdfUrl(c);
        if (url) openUrlInNewTab(url);
        return;
      }
      const form = contratData(c);
      if (!form) throw new Error("Contenu du contrat introuvable — rouvrez le contrat puis ré-enregistrez-le.");
      const { genererContratPdf } = await import('../contract/generatePdf');
      await genererContratPdf(c.structure, form, fileNameFor(c));
    } catch (e: any) {
      toast.error(`Échec téléchargement : ${e.message || e}`);
    } finally {
      setLoadingId(null);
    }
  };

  const apercuContrat = async (c: ContratRow) => {
    try {
      const form = contratData(c);
      if (!form) throw new Error("Contenu du contrat introuvable — rouvrez le contrat puis ré-enregistrez-le.");
      const { previewContratHtml } = await import('../contract/generatePdf');
      previewContratHtml(c.structure, form);
    } catch (e: any) {
      toast.error(`Échec aperçu : ${e.message || e}`);
    }
  };

  const envoyer = async (c: ContratRow) => {
    if (!c.client_email) { toast.error('Email client manquant'); return; }
    setLoadingId(c.id);
    try {
      const pdfUrl = await ensurePdfUrl(c, 60 * 60 * 24 * 30);
      const { error } = await supabase.functions.invoke('send-contrat-email', {
        body: {
          contratId: c.id, numero: c.numero, clientNom: c.client_nom, clientEmail: c.client_email,
          dateEvent: c.date_event ? formatDateFR(c.date_event) : undefined,
          totalTTC: c.montant_ttc, pdfUrl,
          structureLabel: STRUCTURE_LABELS[c.structure],
        },
      });
      if (error) throw error;
      await supabase.from('contrats').update({ statut: 'Transmis' }).eq('id', c.id);
      await resetRelanceNotificationForPrestation(c.prestation_id);
      toast.success(`Contrat envoyé à ${c.client_email}`);
      await loadContrats();
    } catch (e: any) {
      toast.error(`Échec envoi : ${e.message || e}`);
    } finally {
      setLoadingId(null);
    }
  };


  const apercuPdf = async (structure: 'cygnature' | 'suivez_vos_reves') => {
    setLoadingId(`preview-pdf-${structure}`);
    try {
      const { genererContratPdf } = await import('../contract/generatePdf');
      const { DEMO_DATA } = await import('../contract/content');
      await genererContratPdf(structure, DEMO_DATA, `apercu-contrat-${structure}.pdf`);
    } catch (e: any) {
      toast.error(`Échec aperçu : ${e.message || e}`);
    } finally {
      setLoadingId(null);
    }
  };

  const apercuHtml = async (structure: 'cygnature' | 'suivez_vos_reves') => {
    try {
      const { previewContratHtml } = await import('../contract/generatePdf');
      const { DEMO_DATA } = await import('../contract/content');
      previewContratHtml(structure, DEMO_DATA);
    } catch (e: any) {
      toast.error(`Échec aperçu : ${e.message || e}`);
    }
  };

  const currentList = tab === 'transmis' ? transmis : tab === 'signes' ? signes : tab === 'perdus' ? perdus : [];

  return (
    <div>
      <PageTitle
        title="Contrats"
        subtitle={filter === 'all' ? 'Vue globale Cygnature + Suivez Vos Rêves' : `Filtré : ${STRUCTURE_LABELS[filter]}`}
        icon={<FileSignature className="text-emerald-400" size={24} strokeWidth={1.5} />}
      />

      <div className="flex flex-wrap gap-2 mb-3">
        <Link to="/crm-pro-evenementiel/contrats/nouveau"
          className="px-3 py-2 rounded-lg text-xs font-medium bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 flex items-center gap-1.5">
          <Plus size={12} strokeWidth={1.5} /> Nouveau contrat
        </Link>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab('a-generer')} className={`px-3 py-2 rounded-lg text-xs font-medium ${tab === 'a-generer' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-slate-900 text-slate-400 border border-slate-800'}`}>
          À préparer ({candidatesAGenerer.length})
        </button>
        <button onClick={() => setTab('transmis')} className={`px-3 py-2 rounded-lg text-xs font-medium ${tab === 'transmis' ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40' : 'bg-slate-900 text-slate-400 border border-slate-800'}`}>
          Brouillons / Transmis ({transmis.length})
        </button>
        <button onClick={() => setTab('signes')} className={`px-3 py-2 rounded-lg text-xs font-medium ${tab === 'signes' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-slate-900 text-slate-400 border border-slate-800'}`}>
          Signés / Clôturés ({signes.length})
        </button>
        <button onClick={() => setTab('perdus')} className={`px-3 py-2 rounded-lg text-xs font-medium ${tab === 'perdus' ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40' : 'bg-slate-900 text-slate-400 border border-slate-800'}`}>
          Refusés / Perdus ({perdus.length})
        </button>
      </div>

      <div className="space-y-2">
        {tab === 'a-generer' && candidatesAGenerer.map((p) => {
          const versions = getVersionsForPrestation(p.id).slice().sort((a, b) => b.version - a.version);
          return (
            <Card key={p.id} className="p-4 flex items-center gap-4 flex-wrap">
              <FileText size={18} className="text-amber-400 shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <StructureBadge code={p.structure} />
                  <span className="text-sm text-slate-200 font-medium">{p.client}</span>
                  {versions.length > 0 && (
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">
                      {versions.length} version{versions.length > 1 ? 's' : ''} de proposition
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {formatDateFR(p.dateEvent)} · {p.lieu || '—'} · {(p.themes?.filter(Boolean).join(' · ') || '') || p.libelle}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {versions.length > 1 ? (
                  <div className="flex flex-col gap-1">
                    {versions.map((v) => (
                      <div key={v.id} className="flex items-center gap-1.5">
                        <span className="text-[10px] text-slate-400 font-mono w-16 text-right">
                          V{v.version} {v.numero ? `· ${v.numero}` : ''}
                        </span>
                        <Link
                          to={`/crm-pro-evenementiel/contrats/nouveau?prestation=${p.id}&proposition=${v.id}&auto=propal-contrat`}
                          className="px-2 py-1 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 rounded-md text-[11px] flex items-center gap-1"
                          title={`PDF Propal V${v.version} + Contrat`}
                        >
                          <Download size={11} strokeWidth={1.5} /> Propal+Contrat
                        </Link>
                        <Link
                          to={`/crm-pro-evenementiel/contrats/nouveau?prestation=${p.id}&proposition=${v.id}`}
                          className="px-2 py-1 bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded-md text-[11px] flex items-center gap-1"
                        >
                          <FileText size={11} strokeWidth={1.5} /> Préparer
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Link
                      to={`/crm-pro-evenementiel/contrats/nouveau?prestation=${p.id}&auto=propal-contrat`}
                      className="px-3 py-1.5 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 rounded-md text-xs flex items-center gap-1.5"
                      title="Génère un PDF unique contenant la proposition + le contrat"
                    >
                      <Download size={12} strokeWidth={1.5} /> Propal+Contrat
                    </Link>
                    <Link
                      to={`/crm-pro-evenementiel/contrats/nouveau?prestation=${p.id}`}
                      className="px-3 py-1.5 bg-amber-500/20 border border-amber-500/40 text-amber-300 rounded-md text-xs flex items-center gap-1.5"
                    >
                      <FileText size={12} strokeWidth={1.5} /> Préparer le contrat
                    </Link>
                  </div>
                )}
              </div>
            </Card>
          );
        })}

        {tab !== 'a-generer' && (
          <Card className="p-0 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-sm text-slate-500">Chargement…</div>
            ) : currentList.length === 0 ? (
              <div className="p-12 text-center text-sm text-slate-500">
                <FileSignature size={32} className="mx-auto mb-3 text-slate-700" strokeWidth={1.5} />
                Aucun contrat dans cette catégorie.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] text-slate-500 uppercase tracking-widest border-b border-slate-800 bg-slate-950/40">
                    <th className="text-left py-3 px-4 font-normal">Statut</th>
                    <th className="text-left py-3 px-4 font-normal">Client / Événement</th>
                    <th className="text-left py-3 px-4 font-normal">Créée le</th>
                    <th className="text-left py-3 px-4 font-normal">Date event</th>
                    <th className="text-right py-3 px-4 font-normal">Total HT</th>
                    <th className="text-left py-3 px-4 font-normal">Version</th>
                    <th className="text-right py-3 px-4 font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentList.map((c) => {
                    const statutNorm = normalizeContratStatut(c.statut);
                    const isSigne = statutNorm === 'Contrat signé' || statutNorm === 'Dossier clôturé' || statutNorm === 'Dossier archivé';
                    const { type, lieu } = eventLabel(c);
                    return (
                      <tr key={c.id} className="border-b border-slate-800/50 last:border-0 hover:bg-slate-800/20">
                        <td className="py-3 px-4">
                          <select
                            value={statutNorm}
                            onChange={(e) => changerStatut(c, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className={`text-xs px-2 py-0.5 rounded-md border ${contratStatutClass(c.statut)} bg-slate-900/40 cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-500/40`}
                            title="Changer le statut du contrat"
                          >
                            {CONTRAT_STATUTS.map((s) => (
                              <option key={s} value={s} className="bg-slate-900 text-slate-100">{s}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <StructureBadge code={c.structure} />
                            <div>
                              <div className="text-slate-200">{c.client_nom || '—'}</div>
                              <div className="text-xs text-slate-500">{type} · {lieu}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-400 text-xs whitespace-nowrap">
                          {c.created_at ? (
                            <>
                              {formatDateFR(c.created_at, { day: '2-digit', month: '2-digit', year: '2-digit' })}
                              <span className="text-slate-500"> · {new Date(c.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                            </>
                          ) : c.date_emission ? (
                            formatDateFR(c.date_emission, { day: '2-digit', month: '2-digit', year: '2-digit' })
                          ) : '—'}
                        </td>
                        <td className="py-3 px-4 text-slate-400">{c.date_event ? formatDateFR(c.date_event) : '—'}</td>
                        <td className="py-3 px-4 text-right text-amber-400 font-medium tabular-nums">{formatEUR(totalHT(c))}</td>
                        <td className="py-3 px-4 text-slate-400 text-xs font-mono">{c.numero}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="inline-flex gap-1">
                            {c.prestation_id && (
                              <Link
                                to={`/crm-pro-evenementiel/contrats/nouveau?prestation=${getPrestationParam(c.prestation_id)}`}
                                title={isSigne ? 'Modifier le contrat (signé)' : 'Éditer'}
                                className="p-1.5 rounded hover:bg-slate-700 text-slate-300"
                              >
                                <Edit3 size={14} strokeWidth={1.5} />
                              </Link>
                            )}
                            {c.prestation_id && (
                              <Link
                                to={`/crm-pro-evenementiel/prestations?id=${c.prestation_id}`}
                                title="Modifier la demande (dossier)"
                                className="p-1.5 rounded hover:bg-slate-700 text-slate-300"
                              >
                                <Calendar size={14} strokeWidth={1.5} />
                              </Link>
                            )}


                            <button onClick={() => apercuContrat(c)} disabled={!c.contenu} title="Aperçu du contrat" className="p-1.5 rounded hover:bg-slate-700 text-slate-300 disabled:opacity-50 disabled:hover:bg-transparent">
                              <Eye size={14} strokeWidth={1.5} />
                            </button>
                            <button onClick={() => telecharger(c)} disabled={loadingId === c.id || (!c.pdf_url && !c.contenu)} title="Télécharger le PDF" className="p-1.5 rounded hover:bg-amber-500/20 text-amber-300 disabled:opacity-50 disabled:hover:bg-transparent">
                              {loadingId === c.id ? <Loader2 size={14} className="animate-spin" strokeWidth={1.5} /> : <Download size={14} strokeWidth={1.5} />}
                            </button>

                            {c.prestation_id && (
                              <Link
                                to={`/crm-pro-evenementiel/contrats/nouveau?prestation=${getPrestationParam(c.prestation_id)}&auto=propal-contrat`}
                                title="Télécharger Propal + Contrat"
                                className="px-2 py-1 rounded bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 text-[11px] flex items-center gap-1"
                              >
                                <RefreshCcw size={12} strokeWidth={1.5} /> Propal+Contrat
                              </Link>
                            )}
                            <button onClick={() => envoyer(c)} disabled={loadingId === c.id || !c.client_email} title="Envoyer" className="p-1.5 rounded hover:bg-sky-500/20 text-sky-300 disabled:opacity-50 disabled:hover:bg-transparent">
                              {loadingId === c.id ? <Loader2 size={14} className="animate-spin" strokeWidth={1.5} /> : <Send size={14} strokeWidth={1.5} />}
                            </button>

                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
        )}

        {tab === 'a-generer' && !candidatesAGenerer.length && (
          <Card className="p-12 text-center text-slate-500">
            <Calendar size={32} className="mx-auto mb-3 text-slate-700" strokeWidth={1.5} />
            Aucun dossier avec proposition ou étape commerciale en cours.
          </Card>
        )}
      </div>
    </div>
  );
}
